import { useState, useCallback, useEffect } from "react";
import {
  readFromStorageCache,
  writeToStorageCache,
  removeFromStorageCache,
} from "../lib/storageCache";

/**
 * Transparent `useState` backed by localStorage. Initial value is read from the cache;
 * every `setState` call also writes through to localStorage. `setState(null)` removes the
 * key. When `key` is null (e.g. no user session), the hook behaves as plain `useState(null)`.
 *
 * For async data with stale-while-revalidate, prefer `useAsyncData` with `{ cacheKey }` instead.
 */
export function useLocalStorageState<T>(key: string | null): [T | null, (value: T | null) => void] {
  const [state, setState] = useState<T | null>(() => (key ? readFromStorageCache<T>(key) : null));

  // Re-read from cache when the key changes (e.g. user ID changes on sign-in/sign-out)
  useEffect(() => {
    setState(key ? readFromStorageCache<T>(key) : null);
  }, [key]);

  const setAndPersist = useCallback(
    (value: T | null) => {
      setState(value);
      if (!key) return;
      if (value === null) {
        removeFromStorageCache(key);
      } else {
        writeToStorageCache(key, value);
      }
    },
    [key]
  );

  return [state, setAndPersist];
}
